'use client';

import { AlertCircle, RefreshCw } from 'lucide-react';
import React from 'react';
import { Button } from '@/components/ui/button';

interface Properties {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; reset: () => void }>;
}

interface State {
  error: Error | null;
  hasError: boolean;
}

export class TweetErrorBoundary extends React.Component<Properties, State> {
  constructor(properties: Properties) {
    super(properties);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console
    console.error('Tweet component error:', error, errorInfo);

    // For DOM manipulation errors, provide a more user-friendly message
    if (error.message?.includes('removeChild') || error.message?.includes('Node')) {
      this.setState({
        error: new Error('Unable to display tweet preview. Please refresh the page.'),
      });
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  override render(): React.ReactNode {
    if (this.state.hasError) {
      const { fallback: Fallback } = this.props;

      if (Fallback) {
        return <Fallback error={this.state.error || new Error('Unknown error')} reset={this.reset} />;
      }

      return <TweetErrorFallback error={this.state.error || new Error('Unknown error')} reset={this.reset} />;
    }

    return this.props.children;
  }
}

// Default error fallback component
export function TweetErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="my-3 rounded-xl border border-border/60 bg-card p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm text-foreground">Unable to load tweet</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {error.message || 'Something went wrong while loading this tweet.'}
          </p>
          <Button variant="outline" size="sm" onClick={reset} className="mt-3">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Try again
          </Button>
        </div>
      </div>
    </div>
  );
}

// Wrapper component for individual tweet errors
export function TweetWithErrorBoundary({ children }: { children: React.ReactNode }) {
  return <TweetErrorBoundary>{children}</TweetErrorBoundary>;
}
